// Per-widget colour storage — the two PURE decisions behind the home board's
// "Add color" feature (2026-09-02, Nika: "add in context menu 'Add color'
// option so users can customize widget colors + suggested colors"): what the
// local cache key looks like, and which source wins at boot.
//
// The IO stays in pages/main.js beside the hidden-widget writer this mirrors
// (`_hiddenWidgetsKey` / `_saveHiddenWidgets` / `_resolveBootHidden`) — the
// localStorage write, the `_savePrefsPatch` debounce and the workspace-scope
// builder all already live there and are shared with the layout and hidden
// sets. Only the decisions live here, because a decision is what a test can
// pin and IO is not: a colour map written under the wrong scope key is a
// cross-workspace leak, and a boot read with the wrong precedence silently
// resurrects a stale colour after a device sync.
//
// ── SCOPE ────────────────────────────────────────────────────────────────
// The key carries the workspace scope for the same reason the hidden set's
// does: a user's board is per workspace, and a shared key would paint one
// workspace's choices onto another's widgets the moment the ids collide (and
// house widget ids collide by construction — every workspace has `greeting`,
// `received`, `app-chat`). The caller passes the scope string it already
// resolved (`_wsScopeForBootRead(root)`), so there is exactly one scope
// resolver in the app and this file cannot drift from it.
export function homeWidgetColorsKey(scope) {
  return `symbols.home.widgetColors.v1.${scope || 'default'}`
}

// ── PRECEDENCE ───────────────────────────────────────────────────────────
// The user's own row (server, cross-device) ▸ the workspace-scoped local
// cache ▸ nothing. Same order as `_resolveBootHidden`, and for the same
// reason: the row is the durable record and the cache is the fast one, so a
// device that has synced must not be dragged back to what this device
// happened to write last.
//
// One deliberate DIFFERENCE from the hidden set: there is no `_nonEmptyMap`
// guard. An empty hidden map is ambiguous — it can mean "the row was created
// by a layout-only write" and accepting it wiped every panel (the 2026-07-28
// corruption) — but an empty colour map is unambiguous: nothing is picked,
// which is the board's default and overwrites no other source. Guarding it
// would instead let a stale cache resurrect a colour the user had cleared on
// another device.
//
// `prefsRow` MUST already be scope-guarded by the caller (`_prefsRowForScope`)
// — a mid-switch mirror holds the PREVIOUS workspace's row.
// `cachedRaw` is the raw localStorage string (or null); parsing lives here so
// a corrupt cache degrades to "no colours" instead of throwing at boot,
// before the first paint, on the page's own onCreate.
export function readHomeWidgetColors(prefsRow, cachedRaw) {
  const fromRow = prefsRow && prefsRow.widget_colors
  if (_isColorMap(fromRow)) return fromRow
  try {
    const parsed = cachedRaw ? JSON.parse(cachedRaw) : null
    if (_isColorMap(parsed)) return parsed
  } catch (_) {
    /* unreadable cache → no colours, never a throw at boot */
  }
  return {}
}

// A colour map is a plain object of `{ widgetId: familyKey }`. An ARRAY is
// rejected explicitly: `typeof [] === 'object'`, and an array reaching a
// `map[id]` lookup would silently resolve every widget to undefined rather
// than surfacing the bad shape.
const _isColorMap = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
